import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { on, type BenchCurrentImage, type BenchImage, type LaunchOptions } from "../api";

/**
 * Which image the bench runs: a DisplayOS build together with the tests layered
 * on it.
 *
 * It was a select of raw tags - nine entries of ONE DisplayOS build (7.2.72)
 * that differed only in their tests, two pointer names and an old tag shape,
 * under "Build to install first", with no word on what the bench holds now or
 * which entry is newest (Darek, 29 Sep 2026: "totally not clear what's going
 * on there"). Now: what the bench is on, one card per image grouped by the
 * DisplayOS version, the newest marked as the nightly's choice, older tests
 * called out, and the odd names folded away.
 */
export default function BuildPicker({
  options,
  images = {},
  current,
  value,
  onChange,
}: {
  options?: LaunchOptions;
  images?: Record<string, BenchImage>;
  current?: BenchCurrentImage | null;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [typing, setTyping] = useState(false);
  const values = options?.values ?? [];
  const onBench = values.filter((v) => v.source === "bench").map((v) => v.value);
  const elsewhere = values.filter((v) => v.source !== "bench").map((v) => v.value);
  const facts = (tag: string): BenchImage =>
    images[tag] ?? { product: tag, version: "", rig: "", tests: "", pointer: false, layered: false, newest: false, last_used: null };
  const main = onBench.filter((tag) => facts(tag).layered);
  const other = onBench.filter((tag) => !facts(tag).layered);
  const newest = main.find((tag) => facts(tag).newest) ?? main[0];
  // One group per product build, in the order the bench listed them.
  const groups: [string, string[]][] = [];
  for (const tag of main) {
    const p = facts(tag).product;
    const g = groups.find(([k]) => k === p);
    if (g) g[1].push(tag);
    else groups.push([p, [tag]]);
  }
  const curFacts = current ? facts(current.tag) : null;
  const olderThanNewest = (tag: string) =>
    !!newest && tag !== newest && facts(tag).product === facts(newest).product && facts(tag).tests !== facts(newest).tests;
  const chosen = value && !typing ? facts(value) : null;

  const name = (f: BenchImage, tag: string) =>
    f.layered ? t("buildPicker.tests", { sha: f.tests }) : f.pointer ? t("buildPicker.pointer", { tag }) : tag;

  const card = (tag: string, source: "bench" | "images") => {
    const f = facts(tag);
    const selected = !typing && value === tag;
    const isCurrent = current?.tag === tag;
    return (
      <label
        key={`${source}:${tag}`}
        className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 ${
          selected
            ? "border-brand-600 bg-sky-50 ring-1 ring-brand-600 dark:bg-sky-950/40"
            : "border-slate-300 bg-white hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900"
        }`}
      >
        <input
          type="radio"
          name="build-picker"
          className="mt-1"
          checked={selected}
          onChange={() => {
            setTyping(false);
            onChange(tag);
          }}
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-medium text-slate-900 dark:text-slate-100">{name(f, tag)}</span>
            {f.newest && <span className="rounded bg-emerald-100 px-1.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">{t("buildPicker.newest")}</span>}
            {isCurrent && <span className="rounded bg-sky-100 px-1.5 text-xs font-medium text-sky-900 dark:bg-sky-900/40 dark:text-sky-200">{t("buildPicker.onBenchNow")}</span>}
            {olderThanNewest(tag) && <span className="rounded bg-amber-100 px-1.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{t("buildPicker.olderTests")}</span>}
            {source === "images" && <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">{t("buildPicker.download")}</span>}
          </span>
          <span className="mt-0.5 block text-xs text-slate-500">
            {[
              f.rig ? t("buildPicker.scripts", { sha: f.rig }) : "",
              f.pointer ? t("buildPicker.pointerWhy") : "",
              !f.layered && !f.pointer ? t("buildPicker.oldShape") : "",
              f.last_used
                ? t("buildPicker.lastUsed", {
                    when: on(f.last_used.at),
                    what: f.last_used.campaign_id ? t("buildPicker.campaign", { id: f.last_used.campaign_id }) : f.last_used.run_name,
                  })
                : t("buildPicker.neverUsed"),
            ].filter(Boolean).join(" · ")}
          </span>
          <span className="mt-0.5 block break-all font-mono text-xs text-slate-400">{tag}</span>
        </span>
      </label>
    );
  };

  return (
    <fieldset className="space-y-2" data-testid="build-picker">
      <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">{t("buildPicker.title")}</legend>
      <p className="text-xs text-slate-500">{t("buildPicker.explain")}</p>

      <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:bg-slate-800/60 dark:text-slate-300" data-testid="build-now">
        {current && curFacts ? (
          <>
            <span className="font-medium">
              {t("buildPicker.nowIs", {
                build: curFacts.version ? `DisplayOS ${curFacts.version}` : curFacts.product || current.tag,
                tests: curFacts.tests || "?",
              })}
            </span>{" "}
            <span className="text-slate-500">
              {current.campaign_id ? (
                <>
                  {t("buildPicker.nowBy")}{" "}
                  <Link className="underline" to={`/campaigns/${current.campaign_id}`}>{t("buildPicker.campaign", { id: current.campaign_id })}</Link>
                  {`, ${on(current.at)}. `}
                </>
              ) : (
                `${t("buildPicker.nowByRun", { run: current.run_name, when: on(current.at) })} `
              )}
              {t("buildPicker.nowCaveat")}
            </span>
          </>
        ) : (
          t("buildPicker.nowUnknown")
        )}
      </p>

      <label
        className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 ${
          !value && !typing
            ? "border-brand-600 bg-sky-50 ring-1 ring-brand-600 dark:bg-sky-950/40"
            : "border-slate-300 bg-white hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900"
        }`}
      >
        <input type="radio" name="build-picker" className="mt-1" checked={!value && !typing}
               onChange={() => { setTyping(false); onChange(""); }} />
        <span>
          <span className="font-medium text-slate-900 dark:text-slate-100">{t("buildPicker.keep")}</span>
          <span className="mt-0.5 block text-xs text-slate-500">
            {curFacts?.tests
              ? t("buildPicker.keepNow", {
                  build: curFacts.version ? `DisplayOS ${curFacts.version}` : curFacts.product,
                  tests: curFacts.tests,
                })
              : t("buildPicker.keepWhy")}
          </span>
        </span>
      </label>

      {groups.map(([product, tags]) => {
        const v = facts(tags[0]).version;
        return (
          <div key={product} className="space-y-2 pt-1">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              {v ? `DisplayOS ${v}` : product}
              {v && <span className="ml-2 font-mono text-xs font-normal text-slate-500">{product}</span>}
            </p>
            {tags.map((tag) => card(tag, "bench"))}
          </div>
        );
      })}

      {chosen && olderThanNewest(value) && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="build-older-warning">
          {t("buildPicker.olderWarning")}
        </p>
      )}

      {elsewhere.length > 0 && (
        <details className="pt-1">
          <summary className="cursor-pointer text-sm text-slate-700 dark:text-slate-300">{t("buildPicker.registry", { count: elsewhere.length })}</summary>
          <div className="mt-2 space-y-2">{elsewhere.slice(0, 30).map((tag) => card(tag, "images"))}</div>
        </details>
      )}

      {other.length > 0 && (
        <details className="pt-1">
          <summary className="cursor-pointer text-sm text-slate-700 dark:text-slate-300">{t("buildPicker.other", { count: other.length })}</summary>
          <div className="mt-2 space-y-2">{other.map((tag) => card(tag, "bench"))}</div>
        </details>
      )}

      <label className="flex flex-wrap items-center gap-2 pt-1 text-sm text-slate-700 dark:text-slate-300">
        <input type="radio" name="build-picker" checked={typing} onChange={() => { setTyping(true); onChange(""); }} />
        {t("buildPicker.typeIt")}
        {typing && (
          <input
            aria-label={t("buildPicker.typeIt")}
            className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-sm dark:border-slate-700 dark:bg-slate-800"
            placeholder="pdc-maint-…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </label>
      {options?.note && <p className="text-xs text-amber-800 dark:text-amber-300">{options.note}</p>}
    </fieldset>
  );
}
